import { useMemo, useState, type FormEvent } from "react";
import { DueInput, DueTag } from "./DueDate";
import { CONTEXTS, NO_CONTEXT, isOverdue } from "./format";
import { useCompleteTask, useCreateTask, useSpaceParam, useTasks, useTrashTask } from "./hooks";
import type { Energy, Task } from "./types";

export function NextView() {
  const space = useSpaceParam();
  const [maxMinutes, setMaxMinutes] = useState("");
  const [energy, setEnergy] = useState<"" | Energy>("");

  const filters = useMemo(
    () => ({
      status: ["next" as const],
      space,
      max_minutes: maxMinutes ? Number(maxMinutes) : undefined,
      energy: energy || undefined,
      sort: "due" as const,
      limit: 200,
    }),
    [space, maxMinutes, energy],
  );

  const { data, isLoading, error } = useTasks(filters);
  const complete = useCompleteTask();
  const trash = useTrashTask();
  const create = useCreateTask();

  const [newTitle, setNewTitle] = useState("");
  const [newContext, setNewContext] = useState("");
  const [newDue, setNewDue] = useState("");

  // Serwer sortuje po terminie, więc sekcja terminów jest już od najpilniejszego.
  const withDue = useMemo(() => (data?.items ?? []).filter((task) => task.due_date), [data]);

  const groups = useMemo(() => {
    const acc = new Map<string, Task[]>();
    for (const task of data?.items ?? []) {
      if (task.due_date) continue;
      const key = task.context ?? NO_CONTEXT;
      acc.set(key, [...(acc.get(key) ?? []), task]);
    }
    return [...acc.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  function onCreate(event: FormEvent) {
    event.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    create.mutate(
      { title, context: newContext || null, space: space ?? null, due_date: newDue || null },
      {
        onSuccess: () => {
          setNewTitle("");
          setNewDue("");
        },
      },
    );
  }

  return (
    <div className="view">
      <form className="capture capture--wrap" onSubmit={onCreate}>
        <input
          className="capture__input"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Nowe next action…"
          aria-label="Nowe next action"
        />
        <select
          className="capture__select"
          value={newContext}
          onChange={(e) => setNewContext(e.target.value)}
          aria-label="Kontekst"
        >
          <option value="">—</option>
          {CONTEXTS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <DueInput value={newDue} onChange={setNewDue} />
        <button className="btn btn--primary" type="submit" disabled={!newTitle.trim()}>
          Dodaj
        </button>
      </form>

      <div className="filters">
        <label className="filters__item">
          Mam
          <select value={maxMinutes} onChange={(e) => setMaxMinutes(e.target.value)}>
            <option value="">dowolnie czasu</option>
            <option value="5">5 min</option>
            <option value="15">15 min</option>
            <option value="30">30 min</option>
            <option value="60">60 min</option>
          </select>
        </label>
        <label className="filters__item">
          Energia
          <select value={energy} onChange={(e) => setEnergy(e.target.value as "" | Energy)}>
            <option value="">dowolna</option>
            <option value="low">low</option>
            <option value="med">med</option>
            <option value="high">high</option>
          </select>
        </label>
      </div>

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać zadań.</p>}
      {data && data.items.length === 0 && <p className="muted">Brak next actions dla tych filtrów.</p>}

      {withDue.length > 0 && (
        <section className="group">
          <h3 className="group__title">
            terminy <span className="group__count">{withDue.length}</span>
          </h3>
          <ul className="rows">
            {withDue.map((task) => (
              <NextRow
                key={task.id}
                task={task}
                showContext
                onDone={() => complete.mutate(task.id)}
                onTrash={() => trash.mutate(task.id)}
              />
            ))}
          </ul>
        </section>
      )}

      {groups.map(([context, tasks]) => (
        <section key={context} className="group">
          <h3 className="group__title">
            {context} <span className="group__count">{tasks.length}</span>
          </h3>
          <ul className="rows">
            {tasks.map((task) => (
              <NextRow
                key={task.id}
                task={task}
                onDone={() => complete.mutate(task.id)}
                onTrash={() => trash.mutate(task.id)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function NextRow({
  task,
  showContext,
  onDone,
  onTrash,
}: {
  task: Task;
  showContext?: boolean;
  onDone: () => void;
  onTrash: () => void;
}) {
  return (
    <li className={`row ${isOverdue(task) ? "row--overdue" : ""}`}>
      <label className="row__check">
        <input
          type="checkbox"
          checked={false}
          onChange={onDone}
          aria-label={`Zrobione: ${task.title}`}
        />
      </label>
      <div className="row__main">
        <span className="row__title">{task.title}</span>
        <span className="row__meta">
          <DueTag task={task} />
          {showContext && task.context && <span>{task.context}</span>}
          {task.time_estimate_min != null && <span>{task.time_estimate_min} min</span>}
          {task.energy && <span>{task.energy}</span>}
          {task.note && <span className="row__note">{task.note}</span>}
        </span>
      </div>
      <div className="row__actions">
        <button className="btn btn--small btn--danger" title="Do kosza" onClick={onTrash}>
          🗑
        </button>
      </div>
    </li>
  );
}
